import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/lib/require-auth";
import { userHasAnyRole } from "@/lib/has-role.server";


const TIERS = ["adult", "child", "infant", "student", "senior", "private", "group"] as const;
type Tier = (typeof TIERS)[number];

/** Public: availability slots for a tour in a date range */
export const getPublicAvailability = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) =>
    z.object({ tourId: z.string().uuid(), from: z.string(), to: z.string() }).parse(i),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Lazily give back seats held by checkouts that were never paid, so an
    // abandoned cart can't make a departure look sold out.
    try {
      const { sweepExpiredReservations } = await import("./booking-inventory.server");
      await sweepExpiredReservations(data.tourId);
    } catch (e) {
      console.warn("[availability] sweep failed (non-fatal)", e);
    }

    const [{ data: slots }, { data: blocked }] = await Promise.all([
      supabaseAdmin
        .from("custom_tour_availability")
        .select("id, date, start_time, capacity, sold, status")
        .eq("tour_id", data.tourId)
        .gte("date", data.from)
        .lte("date", data.to)
        .order("date")
        .order("start_time"),
      supabaseAdmin
        .from("custom_tour_blocked_dates")
        .select("date, reason")
        .eq("tour_id", data.tourId)
        .gte("date", data.from)
        .lte("date", data.to),
    ]);
    const blockedSet = new Set((blocked ?? []).map((b) => b.date));
    return {
      slots: (slots ?? [])
        .filter((s) => !blockedSet.has(s.date))
        .map((s) => ({
          id: s.id,
          date: s.date,
          time: s.start_time,
          capacity: s.capacity,
          sold: s.sold,
          available: Math.max(0, s.capacity - s.sold),
          status: s.status,
        })),
      blocked: blocked ?? [],
    };
  });

/** Public: resolve pricing for a date (season-aware, falls back to base tour prices) */
export const resolvePricingForDate = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) =>
    z.object({ tourId: z.string().uuid(), date: z.string() }).parse(i),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Find a matching season for this date
    const { data: seasons } = await supabaseAdmin
      .from("custom_tour_seasons")
      .select("id, name, priority, start_date, end_date, active")
      .eq("tour_id", data.tourId)
      .eq("active", true)
      .lte("start_date", data.date)
      .gte("end_date", data.date)
      .order("priority", { ascending: false })
      .limit(1);

    const season = seasons?.[0];
    let prices: Record<Tier, number> = {} as Record<Tier, number>;
    let currency = "MXN";
    let source: "season" | "base" = "base";

    if (season) {
      const { data: sp } = await supabaseAdmin
        .from("custom_tour_season_pricing")
        .select("tier, base_price, currency")
        .eq("season_id", season.id);
      for (const row of sp ?? []) {
        prices[row.tier as Tier] = Number(row.base_price);
        currency = row.currency ?? currency;
      }
      source = "season";
    }

    // Fallback to base custom_tour_pricing
    if (Object.keys(prices).length === 0) {
      const { data: bp } = await supabaseAdmin
        .from("custom_tour_pricing")
        .select("tier, base_price, currency")
        .eq("tour_id", data.tourId);
      for (const row of bp ?? []) {
        prices[row.tier as Tier] = Number(row.base_price);
        currency = row.currency ?? currency;
      }
    }

    return {
      currency,
      source,
      season: season ? { id: season.id, name: season.name } : null,
      prices,
    };
  });

/** Admin/Owner: cancel a booking, release capacity, log modification */
export const adminCancelBooking = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ bookingId: z.string().uuid(), reason: z.string().max(500).optional() }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const allowed = await userHasAnyRole(context.supabase, context.userId, ["admin", "owner"]);
    if (!allowed) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: booking, error: bErr } = await supabaseAdmin
      .from("custom_bookings")
      .select("id, status, travelers, availability_id")
      .eq("id", data.bookingId)
      .maybeSingle();
    if (bErr || !booking) throw new Error("Booking not found");
    if (booking.status === "cancelled") return { ok: true, alreadyCancelled: true };

    const before = { status: booking.status };
    const { error: uErr } = await supabaseAdmin
      .from("custom_bookings")
      .update({
        status: "cancelled",
        cancelled_at: new Date().toISOString(),
        cancellation_reason: data.reason ?? null,
      })
      .eq("id", data.bookingId);
    if (uErr) throw uErr;

    if (booking.availability_id) {
      await supabaseAdmin.rpc("release_availability", {
        _avail_id: booking.availability_id,
        _qty: booking.travelers,
      });
    }

    await supabaseAdmin.from("booking_modifications").insert({
      booking_id: data.bookingId,
      action: "cancel",
      reason: data.reason ?? null,
      before,
      after: { status: "cancelled" },
      performed_by: context.userId,
    });

    return { ok: true };
  });

/** Admin/Owner: CSV export of bookings (returns string) */
export const exportBookingsCSV = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ from: z.string().optional(), to: z.string().optional() }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { requireAdminMfa } = await import("./staff-gate.server");
    await requireAdminMfa(context, "bookings_csv_export");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("custom_bookings")
      .select(
        "booking_code, tour_title, customer_name, customer_email, customer_phone, travel_date, travelers, currency, subtotal, discount_total, total, status, source, commission_pct, created_at",
      )
      .order("created_at", { ascending: false });
    if (data.from) q = q.gte("created_at", data.from);
    if (data.to) q = q.lte("created_at", data.to);
    const { data: rows, error } = await q;
    if (error) throw error;

    const headers = [
      "booking_code","tour_title","customer_name","customer_email","customer_phone",
      "travel_date","travelers","currency","subtotal","discount_total","total",
      "status","source","commission_pct","created_at",
    ];
    const { csvSafeCell: esc } = await import("@/lib/postgrest-safe");
    const lines = [headers.join(",")];
    for (const r of rows ?? []) lines.push(headers.map((h) => esc((r as Record<string, unknown>)[h])).join(","));
    const { audit } = await import("./staff-guard.server");
    await audit(
      { userId: context.userId, claims: context.claims as Record<string, any> },
      {
        action: "bookings_csv_export",
        entityType: "custom_bookings",
        after: { count: rows?.length ?? 0, from: data.from ?? null, to: data.to ?? null },
      },
    );
    return { csv: lines.join("\n"), count: rows?.length ?? 0 };
  });

async function requireAdmin(ctx: { supabase: any; userId: string }) {
  const ok = await userHasAnyRole(ctx.supabase, ctx.userId, ["admin", "owner"]);
  if (!ok) throw new Error("Forbidden");
}

/** Admin/Owner: assign provider + commission % to a booking */
export const assignBookingProvider = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        bookingId: z.string().uuid(),
        providerId: z.string().uuid().nullable(),
        commissionPct: z.number().min(0).max(100).nullable().optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: before } = await supabaseAdmin
      .from("custom_bookings")
      .select("provider_id, commission_pct")
      .eq("id", data.bookingId)
      .maybeSingle();
    const { error } = await supabaseAdmin
      .from("custom_bookings")
      .update({
        provider_id: data.providerId,
        commission_pct: data.commissionPct ?? null,
      })
      .eq("id", data.bookingId);
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("booking_modifications").insert({
      booking_id: data.bookingId,
      action: "assign_provider",
      reason: null,
      before: before ?? {},
      after: { provider_id: data.providerId, commission_pct: data.commissionPct ?? null },
      performed_by: context.userId,
    });
    return { ok: true };
  });

/** Admin/Owner: operational reports (revenue by month, by tour, by provider) */
export const getBookingReports = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ from: z.string().optional(), to: z.string().optional() }).parse(i ?? {}),
  )
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("custom_bookings")
      .select("id, tour_title, total, currency, status, source, provider_id, commission_pct, paid_at, created_at, travelers")
      .in("status", ["paid", "confirmed", "completed"])
      .order("created_at", { ascending: false })
      .limit(5000);
    if (data.from) q = q.gte("created_at", data.from);
    if (data.to) q = q.lte("created_at", data.to);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);

    const { data: providers } = await supabaseAdmin.from("providers").select("id, name");
    const provMap = new Map((providers ?? []).map((p) => [p.id, p.name]));

    const byMonth = new Map<string, { revenue: number; bookings: number; pax: number }>();
    const byTour = new Map<string, { revenue: number; bookings: number; pax: number }>();
    const byProvider = new Map<string, { revenue: number; commission: number; bookings: number }>();
    const bySource = new Map<string, { revenue: number; bookings: number }>();

    let totalRevenue = 0;
    let totalCommission = 0;
    let totalPax = 0;

    for (const r of rows ?? []) {
      const total = Number(r.total ?? 0);
      const pax = Number(r.travelers ?? 0);
      const month = (r.paid_at ?? r.created_at ?? "").slice(0, 7);
      const commission = total * (Number(r.commission_pct ?? 0) / 100);
      totalRevenue += total;
      totalCommission += commission;
      totalPax += pax;

      const m = byMonth.get(month) ?? { revenue: 0, bookings: 0, pax: 0 };
      m.revenue += total; m.bookings += 1; m.pax += pax;
      byMonth.set(month, m);

      const t = byTour.get(r.tour_title ?? "—") ?? { revenue: 0, bookings: 0, pax: 0 };
      t.revenue += total; t.bookings += 1; t.pax += pax;
      byTour.set(r.tour_title ?? "—", t);

      const provName = r.provider_id ? (provMap.get(r.provider_id) ?? "—") : "Sin proveedor";
      const p = byProvider.get(provName) ?? { revenue: 0, commission: 0, bookings: 0 };
      p.revenue += total; p.commission += commission; p.bookings += 1;
      byProvider.set(provName, p);

      const s = bySource.get(r.source ?? "custom") ?? { revenue: 0, bookings: 0 };
      s.revenue += total; s.bookings += 1;
      bySource.set(r.source ?? "custom", s);
    }

    return {
      totals: {
        revenue: totalRevenue,
        commission: totalCommission,
        bookings: rows?.length ?? 0,
        pax: totalPax,
      },
      byMonth: Array.from(byMonth.entries()).map(([k, v]) => ({ key: k, ...v })).sort((a, b) => a.key.localeCompare(b.key)),
      byTour: Array.from(byTour.entries()).map(([k, v]) => ({ key: k, ...v })).sort((a, b) => b.revenue - a.revenue).slice(0, 30),
      byProvider: Array.from(byProvider.entries()).map(([k, v]) => ({ key: k, ...v })).sort((a, b) => b.revenue - a.revenue),
      bySource: Array.from(bySource.entries()).map(([k, v]) => ({ key: k, ...v })),
    };
  });

/** Admin/Owner: list providers (lightweight, for selects) */
export const listProvidersLite = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await requireAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("providers")
      .select("id, name, default_commission_pct")
      .eq("active", true)
      .order("name");
    return { providers: data ?? [] };
  });

/** Admin/Owner: booking detail + modifications history */
export const getBookingDetail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ bookingId: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { requireAdminMfa } = await import("./staff-gate.server");
    await requireAdminMfa(context, "booking_detail");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: booking }, { data: mods }] = await Promise.all([
      supabaseAdmin.from("custom_bookings").select("*").eq("id", data.bookingId).maybeSingle(),
      supabaseAdmin
        .from("booking_modifications")
        .select("*")
        .eq("booking_id", data.bookingId)
        .order("created_at", { ascending: false }),
    ]);
    return { booking, modifications: mods ?? [] };
  });

/**
 * Admin/Owner: retry the Bokun reservation for a booking that was paid but
 * never reserved (Bokun outage, or an automatic refund that also failed).
 * Uses the same atomic claim as the checkout paths, so it can never create a
 * second reservation for the same payment.
 */
export const retryBokunReservation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ bookingId: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: booking } = await supabaseAdmin
      .from("custom_bookings")
      .select("id, status, source, bokun_activity_id, bokun_confirmation_code, metadata")
      .eq("id", data.bookingId)
      .maybeSingle();
    if (!booking) return { ok: false, error: "Reserva no encontrada." };
    if (booking.bokun_confirmation_code) {
      return { ok: true, error: null, confirmationCode: booking.bokun_confirmation_code };
    }
    if (booking.source !== "bokun" || !booking.bokun_activity_id) {
      return { ok: false, error: "Esta reserva no es de Bokun." };
    }
    if (booking.status !== "paid") {
      return { ok: false, error: `Solo se puede reintentar una reserva pagada (estado actual: ${booking.status}).` };
    }
    const md = (booking.metadata ?? {}) as Record<string, unknown>;
    const cart = (md.cart ?? []) as Array<Record<string, unknown>>;
    const customer = (md.customer ?? {}) as Record<string, unknown>;
    if (!cart.length) return { ok: false, error: "La reserva no tiene datos de carrito para reintentar." };

    const { reserveInBokun, claimBokunReservation } = await import("@/lib/bokun-reserve.server");
    const claim = await claimBokunReservation(data.bookingId);
    if (claim === "done") return { ok: true, error: null, confirmationCode: null };
    if (claim !== "claimed") {
      return { ok: false, error: "La reserva se está procesando en este momento. Inténtalo de nuevo en unos minutos." };
    }
    const result = await reserveInBokun(cart as never, customer);
    if (result.ok && result.confirmationCode) {
      await supabaseAdmin
        .from("custom_bookings")
        .update({
          status: "confirmed",
          bokun_confirmation_code: result.confirmationCode,
          bokun_booking_id: result.bookingId,
          notes: "Reserva completada por reintento manual desde el panel.",
        })
        .eq("id", data.bookingId);
      try {
        const { sendBookingConfirmationEmail } = await import("@/lib/booking-email.server");
        await sendBookingConfirmationEmail(data.bookingId);
      } catch (e) {
        console.warn("[retryBokun] email failed", e);
      }
      return { ok: true, error: null, confirmationCode: result.confirmationCode };
    }
    // Release the claim so it can be retried again.
    await supabaseAdmin
      .from("custom_bookings")
      .update({ status: "paid", notes: `Reintento manual falló: ${result.error ?? "error desconocido"}` })
      .eq("id", data.bookingId);
    return { ok: false, error: result.error ?? "Bokun rechazó la reserva." };
  });

/** Admin/Owner: last voucher email attempt per booking (for the bookings table) */
export const getVoucherStatuses = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ bookingIds: z.array(z.string().uuid()).max(200) }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    if (!data.bookingIds.length) return { statuses: {} as Record<string, { status: string; error: string | null; attempt: number; createdAt: string }> };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows } = await supabaseAdmin
      .from("booking_notifications")
      .select("booking_id, status, error_message, attempt, created_at")
      .eq("channel", "email")
      .eq("event", "voucher")
      .in("booking_id", data.bookingIds)
      .order("created_at", { ascending: false });
    const statuses: Record<string, { status: string; error: string | null; attempt: number; createdAt: string }> = {};
    for (const r of rows ?? []) {
      const key = String(r.booking_id);
      // rows come newest-first: keep the first (latest) attempt, but a later
      // `sent` for the same booking wins over a failure.
      const prev = statuses[key];
      if (!prev || (prev.status !== "sent" && r.status === "sent")) {
        statuses[key] = {
          status: String(r.status),
          error: r.error_message ?? null,
          attempt: Number(r.attempt ?? 1),
          createdAt: String(r.created_at),
        };
      }
    }
    return { statuses };
  });

/** Admin/Owner: resend the voucher email for a booking. */
export const resendBookingVoucher = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ bookingId: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const { sendBookingVoucherEmail } = await import("@/lib/booking-email.server");
    const res = await sendBookingVoucherEmail(data.bookingId, "voucher");
    return { ok: res.status === "sent", error: res.error, recipient: res.recipient };
  });
